import { Phone, Mail, MapPin, Linkedin } from "lucide-react";
import { Link } from "react-router-dom";
import { STAFF_LOGIN_URL } from "@/lib/constants";
import { openCookieBanner } from "@/lib/cookieConsent";
import logoWhite from "@/assets/finable-logo-white.svg";

function InstagramIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
      <circle cx="12" cy="12" r="5" />
      <circle cx="17.5" cy="6.5" r="1.5" fill="currentColor" stroke="none" />
    </svg>
  );
}

function YouTubeIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    </svg>
  );
}

function FacebookIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  );
}


export default function Footer() {
  return (
    <footer className="relative bg-gradient-hero text-white/80 overflow-hidden">
      {/* decorative accent glow */}
      <div className="pointer-events-none absolute -top-32 -right-32 w-96 h-96 rounded-full bg-accent/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -left-20 w-[28rem] h-[28rem] rounded-full bg-accent/5 blur-3xl" />

      <div className="relative container pt-20 pb-12 grid md:grid-cols-2 lg:grid-cols-4 gap-12">
        <div className="lg:col-span-1">
          <img src={logoWhite} alt="FINABLE Finanzberatung Logo" width={120} height={36} loading="lazy" decoding="async" className="h-9 w-auto" />
          <p className="mt-5 text-sm leading-relaxed text-white/70 max-w-xs">
            FINABLE – modern, kompetent & zeitgemäß. Zuverlässige, unabhängige Beratung für renditeorientierten Vermögensaufbau & beständige Absicherung.
          </p>
          <div className="mt-5 flex items-center gap-3">
            <a
              href="https://www.instagram.com/fin_able/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/70 hover:text-accent hover:border-accent/40 transition-colors"
              aria-label="Instagram"
            >
              <InstagramIcon className="w-3.5 h-3.5" />
            </a>
            <a
              href="https://www.facebook.com/finablegmbh"
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/70 hover:text-accent hover:border-accent/40 transition-colors"
              aria-label="Facebook"
            >
              <FacebookIcon className="w-3.5 h-3.5" />
            </a>
            <a
              href="https://www.linkedin.com/company/finable-gmbh/?viewAsMember=true"
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/70 hover:text-accent hover:border-accent/40 transition-colors"
              aria-label="LinkedIn"
            >
              <Linkedin className="w-3.5 h-3.5" />
            </a>
            <a
              href="https://www.youtube.com/@finable1214"
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/70 hover:text-accent hover:border-accent/40 transition-colors"
              aria-label="YouTube"
            >
              <YouTubeIcon className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>

        <div>
          <h3 className="text-accent font-semibold uppercase tracking-wider text-sm mb-5">Links</h3>
          <ul className="space-y-3 text-sm text-white/80">
            <li><Link to="/" className="hover:text-accent transition-colors">Start</Link></li>
            <li><Link to="/leistungen" className="hover:text-accent transition-colors">Leistungen</Link></li>
            <li><Link to="/team" className="hover:text-accent transition-colors">Unser Team</Link></li>
            <li><Link to="/kundenstimmen" className="hover:text-accent transition-colors">Kundenstimmen</Link></li>
            <li><Link to="/karriere" className="hover:text-accent transition-colors">Karriere</Link></li>
            <li><Link to="/blog" className="hover:text-accent transition-colors">Blog</Link></li>
            <li><Link to="/glossar" className="hover:text-accent transition-colors">Glossar</Link></li>
            <li><Link to="/empfehlung" className="hover:text-accent transition-colors">Empfehlung</Link></li>
          </ul>
        </div>

        <div>
          <h3 className="text-accent font-semibold uppercase tracking-wider text-sm mb-5">Rechtliches</h3>
          <ul className="space-y-3 text-sm text-white/80">
            <li><a href="/impressum" className="hover:text-accent transition-colors">Impressum</a></li>
            <li><a href="/datenschutz" className="hover:text-accent transition-colors">Datenschutz</a></li>
            <li><a href="/barrierefreiheit" className="hover:text-accent transition-colors">Barrierefreiheit</a></li>
            <li><a href="/erstinformation" className="hover:text-accent transition-colors">Erstinformation</a></li>
            <li>
              <button type="button" onClick={openCookieBanner} className="text-left hover:text-accent transition-colors">
                Cookie-Einstellungen
              </button>
            </li>
          </ul>
        </div>

        <div>
          <h3 className="text-accent font-semibold uppercase tracking-wider text-sm mb-5">Kontakt</h3>
          <ul className="space-y-4 text-sm text-white/80">
            <li className="flex items-start gap-3">
              <span className="mt-0.5 w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                <Phone className="w-3.5 h-3.5 text-accent" />
              </span>
              <a href="tel:+494057249358" className="hover:text-accent transition-colors leading-8">040 5724 9358</a>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-0.5 w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                <Mail className="w-3.5 h-3.5 text-accent" />
              </span>
              <a href="mailto:moin@finable.de" className="hover:text-accent transition-colors leading-8">moin@finable.de</a>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-0.5 w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                <MapPin className="w-3.5 h-3.5 text-accent" />
              </span>
              <span>Rothenbaumchaussee 58<br />20148 Hamburg</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="relative border-t border-white/10">
        <div className="container py-5">
          <p className="text-[12px] leading-relaxed text-white/60">
            FINABLE bietet unabhängige Finanzberatung zu den Themen Altersvorsorge, Steueroptimierung, Vermögensaufbau, staatliche Förderungen, Rürup, Riester, ETF-Investments, betriebliche Altersvorsorge und Absicherung – digital und bundesweit.
          </p>
        </div>
        <div className="container pb-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/60 border-t border-white/10 pt-4">
          <p>© {new Date().getFullYear()} FINABLE. Alle Rechte vorbehalten.</p>
          <a href={STAFF_LOGIN_URL} target="_blank" rel="noopener noreferrer" className="hover:text-accent transition-colors">
            Mitarbeiter Login →
          </a>
        </div>
      </div>
    </footer>
  );
}
